import { useQueryClient } from '@tanstack/react-query';
import { sum } from 'd3';
import type { TFunction } from 'i18next';
import React, {
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, useTheme } from 'styled-components';
import { Matrix4Tuple, Vector3 } from 'three';
import { throttle } from 'underscore';

import {
  GeodataImage,
  GeodataImageExifData,
} from '../../../../../typings/api/skymap/rest/v0/geodata';
import { GeodataImageUpdatedMessage } from '../../../../../typings/websocket-messages';
import { CloudFrontSigner } from '../../../utils/cloud-front-signer/cloud-front-signer';
import {
  getJpegExifData,
  jpegExifDataHasGpsInformation,
  jpegExifExtractCameraTransform,
} from '../../../utils/file-utils';
import { toHumanReadableFileSize } from '../../../utils/io/file';
import { normalizeString } from '../../../utils/text/text';
import { isDefined, isIncluded } from '../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useSubscribeCallback } from '../../hooks/use-subscribe';
import { Center } from '../../styles/center';
import {
  GeodataImageStatusIcon,
  GeodataImageUseGpsIcon,
} from './geodata-image-list/geodata-image-status-icon';
import { LocalGeodataImage, LocalGeodataImageStatus } from './geodata-image-list/types';
import { useGetGeodataImageListQuery } from './geodata-image-list/use-get-geodata-image-list-query';
import {
  UploadImageParameters,
  useUploadImageQueue,
} from './geodata-image-list/use-image-upload-queue';
import { GeodataContext } from './geodata-state';
import { useGeodataCloudFrontSigner } from './use-geodata-cloudfront-signer';

export interface GeodataImagesContext {
  images: GeodataImageItem[];
  progress: GeodataImageUploadProgress;
  isPendingGetImages: boolean;
  uploadImage: ReturnType<typeof useUploadImageCallback>;
  linkLocalImages: ReturnType<typeof useLinkLocalImagesCallback>;
  refetchImages: () => void;
}

export const GeodataImagesContext = React.createContext<GeodataImagesContext>(undefined!);

const minImageCount = 3;

type GeodataImageListState = 'idle' | 'pending' | 'completed' | 'failed';

export type GeodataImageUploadProgress = {
  text: string;
  value: number;
  state: GeodataImageListState;
};

export type GeodataImageItem = {
  imageIdentifier: string;
  statusIcon: ReactNode;
  useGpsIcon: ReactNode;
  displayName: string;
  status: LocalGeodataImageStatus;
  transform?: Matrix4Tuple;
  aligned: boolean;
  enabled: boolean;
  useGps: boolean;
  file?: File;
  imageId?: string;
  standardDeviation: [number, number, number];
  totalStandardDeviation: number;
  exifData?: GeodataImageExifData;
};

type DispatchItemAction = {
  type: 'UPDATE';
  theme: DefaultTheme;
  t: TFunction;
  items: LocalGeodataImage[];
};

function mapImage(image: LocalGeodataImage, theme: DefaultTheme, t: TFunction): GeodataImageItem {
  return {
    imageIdentifier: image.name,
    imageId: image.imageId,
    displayName: image.name.substring(image.name.indexOf('_') + 1),
    status: image.status,
    statusIcon: GeodataImageStatusIcon(image, theme, t),
    useGpsIcon: GeodataImageUseGpsIcon(image, theme),
    transform: image.transform,
    aligned: image.aligned,
    enabled: image.enabled,
    useGps: image.useGps,
    file: image.file,
    standardDeviation: image.deviation,
    totalStandardDeviation: new Vector3(...image.deviation).length(),
    exifData: image.exifData,
  };
}

function useSubscribeToGeodataImageUpdatedMessage(
  geodataId: string,
  callback: (message: GeodataImageUpdatedMessage) => void,
) {
  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (event.message === 'GeodataImageUpdated' && event.geodataId === geodataId) {
      callback(event);
    }
  });
}

function itemsReducer(state: GeodataImageItem[], action: DispatchItemAction): GeodataImageItem[] {
  if (action.type === 'UPDATE') {
    const newState: GeodataImageItem[] = state.map((existingItem) => {
      const updatedItem = action.items.find(
        (newItem) => newItem.name === existingItem.imageIdentifier,
      );

      if (!isDefined(updatedItem)) {
        return existingItem;
      }

      // Make sure locally attached file remains attached.
      if (isDefined(existingItem.file)) {
        updatedItem.file = existingItem.file;
      }

      // Make sure transform remains attached if defined.
      if (isDefined(existingItem.transform) && !isDefined(updatedItem.transform)) {
        updatedItem.transform = existingItem.transform;
      }

      return mapImage(updatedItem, action.theme, action.t);
    });

    action.items.forEach((newItem) => {
      if (!state.some((existingItem) => newItem.name === existingItem.imageIdentifier)) {
        newState.push(mapImage(newItem, action.theme, action.t));
      }
    });

    return newState;
  }

  return state;
}

function useGeodataImageProgress(items: GeodataImageItem[]): GeodataImageUploadProgress {
  return useMemo(() => {
    const failed = items.filter((x) => isIncluded(['FAILED', 'UPLOAD_FAILED'], x.status));
    const pending = items.filter((x) => isIncluded(['PENDING', 'UPLOADING'], x.status));
    const uploaded = items.filter((x) => isIncluded(['COMPLETED'], x.status));
    const incompleteCount = pending.length + failed.length;

    const totalSize = sum(items.map((x) => x.file?.size ?? 0));
    const uploadedSize = sum(uploaded.map((x) => x.file?.size ?? 0));

    return {
      text: `${items.length - incompleteCount} / ${items.length.toString()} (${toHumanReadableFileSize(uploadedSize)} / ${toHumanReadableFileSize(totalSize)})`,
      value: items.length > 0 ? 1 - incompleteCount / items.length : 0,
      state:
        failed.length > 0
          ? 'failed'
          : pending.length > 0
            ? 'pending'
            : uploaded.length <= minImageCount
              ? 'idle'
              : 'completed',
    };
  }, [items]);
}

/**
 * Gets a unique identifier for the image by combining date time original with the file name.
 */
export async function parseImage(file: File): Promise<{
  imageIdentifier: string;
  transform?: Matrix4Tuple;
  errorStatus?: LocalGeodataImageStatus;
  position?: { longitude: number; latitude: number };
}> {
  const normalizedFileName = normalizeString(file.name);
  try {
    const exifData = await getJpegExifData(file);

    if (!jpegExifDataHasGpsInformation(exifData)) {
      return {
        imageIdentifier: `${file.lastModified}_${normalizedFileName}`,
        errorStatus: 'FAILED_MISSING_GPS_DATA',
      };
    }

    if (!isDefined(exifData.tags?.DateTimeOriginal)) {
      return {
        imageIdentifier: `${file.lastModified}_${normalizedFileName}`,
        errorStatus: 'FAILED_MISSING_CAPTURE_DATE',
      };
    }

    const timestamp = exifData.tags.DateTimeOriginal.toString();
    const longitude = exifData.tags?.GPSLongitude;
    const latitude = exifData.tags?.GPSLatitude;

    let position: { longitude: number; latitude: number } | undefined;
    if (isDefined(longitude) && isDefined(latitude)) {
      position = { longitude, latitude };
    }

    return {
      imageIdentifier: normalizedFileName.startsWith(timestamp)
        ? normalizedFileName
        : `${timestamp}_${normalizedFileName}`,
      transform: jpegExifExtractCameraTransform(exifData),
      position,
    };
  } catch {
    return {
      imageIdentifier: `${file.lastModified}_${normalizedFileName}`,
      errorStatus: 'FAILED',
    };
  }
}

function useUploadImageCallback(
  items: GeodataImageItem[],
  dispatchItems: Dispatch<DispatchItemAction>,
  queueUpload: (params: UploadImageParameters) => void,
  signer: CloudFrontSigner | undefined,
  theme: DefaultTheme,
  t: TFunction,
) {
  return useCallback(
    async (file: File) => {
      if (!isDefined(signer)) {
        throw Error('Unable to upload image. CloudFrontSigner is not initiated.');
      }

      const { imageIdentifier, errorStatus, transform } = await parseImage(file);
      const matchedImage = items.find(
        (x) => x.imageIdentifier === imageIdentifier && x.status !== 'UPLOAD_FAILED',
      );

      if (!isDefined(matchedImage?.file)) {
        dispatchItems({
          items: [
            {
              name: imageIdentifier,
              imageId: matchedImage?.imageId,
              status: errorStatus ?? matchedImage?.status ?? 'UPLOADING',
              transform: matchedImage?.transform ?? transform,
              aligned: matchedImage?.aligned ?? true,
              enabled: matchedImage?.enabled ?? true,
              useGps: matchedImage?.useGps ?? true,
              file,
              deviation: matchedImage?.standardDeviation ?? [0, 0, 0],
              exifData: matchedImage?.exifData ?? undefined,
            },
          ],
          theme,
          t,
          type: 'UPDATE',
        });
      }

      // Don't queue upload if there is a matched image or if there was an error parsing it.
      if (isDefined(matchedImage) || isDefined(errorStatus)) {
        return;
      }

      queueUpload({
        imageIdentifier,
        file,
        signer: signer,
        onError: () => {
          dispatchItems({
            items: [
              {
                name: imageIdentifier,
                imageId: undefined,
                status: 'UPLOAD_FAILED',
                transform: undefined,
                aligned: true,
                enabled: true,
                useGps: true,
                file,
                deviation: [0, 0, 0],
              },
            ],
            theme,
            t,
            type: 'UPDATE',
          });
        },
        onSuccess: () => {
          dispatchItems({
            items: [
              {
                name: imageIdentifier,
                imageId: undefined,
                status: 'PENDING',
                transform: undefined,
                aligned: true,
                enabled: true,
                useGps: true,
                file,
                deviation: [0, 0, 0],
              },
            ],
            theme,
            t,
            type: 'UPDATE',
          });
        },
      });
    },
    [items, dispatchItems, queueUpload, signer, theme, t],
  );
}

/**
 * Links local images up with the uploaded images to speed up the loading on slow connections.
 */
function useLinkLocalImagesCallback(
  items: GeodataImageItem[],
  dispatchItems: Dispatch<DispatchItemAction>,
  theme: DefaultTheme,
) {
  const { t } = useTranslation();
  return useCallback(
    async (files: File[]) => {
      const imagesToLink: LocalGeodataImage[] = [];
      for (const file of files) {
        const { imageIdentifier, errorStatus } = await parseImage(file);
        const matchingImage = items.find((x) => x.imageIdentifier === imageIdentifier);
        if (isDefined(matchingImage) && !isDefined(matchingImage.file) && !isDefined(errorStatus)) {
          imagesToLink.push({
            name: imageIdentifier,
            imageId: matchingImage?.imageId,
            status: matchingImage.status,
            aligned: matchingImage.aligned,
            enabled: matchingImage.enabled,
            useGps: matchingImage.useGps,
            transform: matchingImage.transform,
            file,
            deviation: matchingImage.standardDeviation,
            exifData: matchingImage.exifData,
          });
        }
      }

      dispatchItems({
        items: imagesToLink,
        theme,
        t,
        type: 'UPDATE',
      });
      publish(SubscriptionTopic.ToastrMessage, {
        type: imagesToLink.length > 0 ? 'success' : 'info',
        message:
          imagesToLink.length > 0
            ? t('imageList.imagesLinked', { ns: 'cloudProcessing' })
            : t('imageList.noImagesLinked', { ns: 'cloudProcessing' }),
      });
    },
    [items, dispatchItems, theme, t],
  );
}

type Props = {
  children: React.ReactNode;
};

export const GeodataImagesState = (props: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { geodata } = useContext(GeodataContext);
  const signer = useGeodataCloudFrontSigner(geodata.id);
  const { images, isPendingGetImages, hasError, refetchImages, buildErrorListImages } =
    useGetGeodataImageListQuery(geodata.id, geodata.photoCount);
  const queueUpload = useUploadImageQueue();
  const [items, dispatchItems] = useReducer(itemsReducer, []);
  const progress = useGeodataImageProgress(items);
  const uploadImage = useUploadImageCallback(items, dispatchItems, queueUpload, signer, theme, t);
  const linkLocalImages = useLinkLocalImagesCallback(items, dispatchItems, theme);

  const contextValue = useMemo<GeodataImagesContext>(() => {
    return {
      images: items,
      isPendingGetImages,
      progress,
      uploadImage,
      linkLocalImages,
      refetchImages,
    };
  }, [items, isPendingGetImages, progress, linkLocalImages, uploadImage, refetchImages]);

  useEffect(() => {
    const items: LocalGeodataImage[] = images.map((x) => {
      return {
        name: x.name,
        imageId: x.id,
        aligned: x.aligned,
        enabled: x.enabled,
        status: 'COMPLETED',
        useGps: x.useGps,
        transform: x.transform as Matrix4Tuple,
        deviation: x.deviation,
        exifData: x.exifData,
      };
    });

    dispatchItems({
      type: 'UPDATE',
      items,
      theme,
      t,
    });
  }, [images, theme, t]);

  useSubscribeCallback(SubscriptionTopic.OnWebsocketReconnected, () => {
    void refetchImages();
  });

  // Coalesce GeodataImageUpdated websocket messages. The backend emits one
  // message per processed image, so bulk uploads produce bursts of hundreds of
  // messages. Applying each individually floods React with re-renders and can
  // starve unrelated work such as in-app navigation (SK-7199). We buffer the
  // updates and flush them as a single batched dispatch + cache write at most
  // once per throttle window.
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const tRef = useRef(t);
  tRef.current = t;
  const pendingUpdatesRef = useRef<GeodataImageUpdatedMessage[]>([]);

  const flushImageUpdates = useCallback(() => {
    const buffered = pendingUpdatesRef.current;
    if (buffered.length === 0) {
      return;
    }
    pendingUpdatesRef.current = [];

    // Keep only the latest update per image so the reducer doesn't append
    // duplicate rows when several messages arrive for the same image.
    const latestByName = new Map<string, GeodataImageUpdatedMessage>();
    for (const update of buffered) {
      latestByName.set(update.imageName, update);
    }
    const updates = [...latestByName.values()];

    dispatchItems({
      type: 'UPDATE',
      items: updates.map((data) => ({
        name: data.imageName,
        imageId: data.id,
        status: data.status,
        aligned: data.aligned,
        enabled: data.enabled,
        useGps: data.useGpsPosition,
        transform: data.transform,
        deviation: data.standardDeviation,
      })),
      theme: themeRef.current,
      t: tRef.current,
    });

    queryClient.setQueriesData<GeodataImage[]>(
      { queryKey: ['getGeodataImages', geodata.id] },
      (oldData) => {
        if (!isDefined(oldData)) {
          return oldData;
        }
        const remaining = new Map(latestByName);
        const updated = oldData.map((image) => {
          const data = remaining.get(image.name);
          if (!isDefined(data)) {
            return image;
          }
          remaining.delete(image.name);
          return {
            ...image,
            id: data.id,
            transform: data.transform ?? image.transform,
            aligned: data.aligned,
            enabled: data.enabled,
            useGps: data.useGpsPosition,
            deviation: data.standardDeviation,
          };
        });
        for (const data of remaining.values()) {
          updated.push({
            id: data.id,
            name: data.imageName,
            transform: data.transform ?? [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
            aligned: data.aligned,
            enabled: data.enabled,
            useGps: data.useGpsPosition,
            deviation: data.standardDeviation,
          });
        }
        return updated;
      },
    );
  }, [dispatchItems, queryClient, geodata.id]);

  const throttledFlushImageUpdates = useMemo(
    () => throttle(flushImageUpdates, 300, { leading: false, trailing: true }),
    [flushImageUpdates],
  );

  useEffect(() => {
    return () => throttledFlushImageUpdates.cancel();
  }, [throttledFlushImageUpdates]);

  useSubscribeToGeodataImageUpdatedMessage(geodata.id, (data) => {
    pendingUpdatesRef.current.push(data);
    throttledFlushImageUpdates();
  });

  if (hasError()) {
    return <Center>{buildErrorListImages()}</Center>;
  }

  return (
    <GeodataImagesContext.Provider value={contextValue}>
      {props.children}
    </GeodataImagesContext.Provider>
  );
};
